// @ts-ignore
import React, { FC, useState, useEffect, useCallback } from 'react';
import { RouteComponentProps } from 'react-router';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { BemMethod, createBem } from '@om-fe/utils';
import { AppState } from '@om-fe/core/core/types';
import { DisciplineRouteType } from '@om-fe/core/components/discipline-anchor/discipline-route.utils';
import { Breadcrumbs } from '../components/breadcrumbs/breadcrumbs';
import { MixedGroupedTournament } from '../components/mixed-grouped/containers/mixed-grouped-tournament/mixed-grouped-tournament';
import { MixedGroupedOddsOutrightsTabSelected } from '../components/mixed-grouped/components/tabs/mixed-grouped-odds-outrights-tabs.types';
import { Breakpoints } from '@om-fe/core/core/store/breakpoints';
import { getMainMarketsLimit, getMainMarketsLimitByWidth } from '@om-fe/core/components/match-list/utils/match-list.utils';
import { PlatformTypes, isBrowser } from '@om-fe/utils';
import UserBehaviorTrackingContext from '@om-fe/core/components/user-behavior-tracking-context/user-behavior-tracking-context';
import { UserBehaviorTrackingLocation } from '@om-fe/core/components/user-behavior-tracking-context/user-behavior-tracking.utils';
import { RouteNames } from '@om-fe/core/core/routing/utils';
import getConfig, { OMFE2ConfigModel } from '@om-fe/core/utils/config';
import { MAIN_MARKET_MIN_LIMIT } from '@om-fe/core/components/match-list/utils/match-list.constants';
import { TournamentParams } from '@om-fe/models';
import { getVirtualStateExtraParams, setVirtualStateExtraParams } from '@om-fe/core/core/routing/custom-routes/custom-routes.utils';
import { withSelectionBetslipData, WithSelectionBetslipDataModel } from '@om-fe/widgets/dist/widgets/betslip/with-betslip-selection-data/with-betslip-selection-data';
import { DeepLink } from '@om-fe/core/utils/deep-link';

interface TournamentPageStoreProps {
    readonly isLarge?: boolean;
    readonly breakpoints?: Breakpoints;
    readonly deviceType?: PlatformTypes;
    readonly routeName?: RouteNames;
}

type TournamentPagePublicProps = RouteComponentProps<{
    readonly disciplineId: string;
    readonly disciplineName: string;
    readonly tournamentName: string;
    readonly tournamentId: string;
    readonly eventCategoryId?: string;
    readonly eventCategoryName?: string;
    readonly locationName?: string;
    readonly locationId?: string;
    readonly activeTab?: string;
    readonly sportLocationId?: string;
    readonly sportLocationName?: string;
    readonly bettingOfferIds?: string;
    readonly stake?: string;
}> & WithSelectionBetslipDataModel;

type TournamentPageProps = TournamentPageStoreProps & TournamentPagePublicProps;

const bem: BemMethod = createBem('TournamentPage');

function TournamentPageComponent(
    {
        isLarge,
        match: { params },
        breakpoints,
        deviceType,
        routeName,
        addSelection,
        setSingleSelectionStake,
        setMasterStake,
    }: TournamentPageProps,
) {
    const [tournamentParams, setTournamentParams] = useState<TournamentParams>(params);

    useEffect(() => {
        const { tournamentId, bettingOfferIds, stake } = params;
        if (params) {
            setTournamentParams(params);
        }

        DeepLink({ bettingOfferIds, stake, id: tournamentId, addSelection, setSingleSelectionStake, setMasterStake });
    }, [params, DeepLink]);

    const handleSetTournamentParams = useCallback((data: TournamentParams) => {
        setTournamentParams(data);
        setVirtualStateExtraParams(data);
    }, []);

    const { 
        eventCategoryId,
        locationId,
        disciplineId,
        tournamentId,
        activeTab,
    } = tournamentParams;
    const storage: TournamentParams = getVirtualStateExtraParams<TournamentParams>();

    let sportPageType: DisciplineRouteType;
    const config: OMFE2ConfigModel = getConfig();
    const getTournamentParams: TournamentParams = typeof storage?.tournamentId !== 'undefined' ? storage : tournamentParams;
    const getActiveTab = storage?.activeTab && (storage?.activeTab === MixedGroupedOddsOutrightsTabSelected.Odds || storage?.activeTab === MixedGroupedOddsOutrightsTabSelected.Outrights) ? storage?.activeTab : activeTab;
    const getTournamentId = storage?.tournamentId ? storage?.tournamentId : tournamentId;
    const mainMarketsLimit: number = config.mainMarketsLimit?.responsive
        ? config.mainMarketsLimit?.byWidth && (isBrowser() && window)
            ? getMainMarketsLimitByWidth(window.innerWidth, config.mainMarketsLimit?.breakpointsByWidth)
            : getMainMarketsLimit(breakpoints)
        : MAIN_MARKET_MIN_LIMIT;

    if (eventCategoryId) {
        sportPageType = DisciplineRouteType.Category;
    } else if (locationId) {
        sportPageType = DisciplineRouteType.Location;
    } else {
        sportPageType = DisciplineRouteType.Parent;
    }

    const showTournamentName: boolean = routeName && routeName !== RouteNames.tournamentLocation && routeName !== RouteNames.tournamentCategories;

    return (
        <div className={bem()}>
            <Breadcrumbs
                disciplineId={disciplineId}
                locationId={locationId}
                tournamentId={tournamentId !== '0' && showTournamentName ? tournamentId : undefined}
                categoryId={eventCategoryId}
                isLarge={isLarge}
                routeType={sportPageType}
            />
            <UserBehaviorTrackingContext.Provider value={{ userBehaviorTrackingPage: RouteNames.tournamentLocation, userBehaviorTrackingLocation: UserBehaviorTrackingLocation.GroupedMatchList }}>
                <MixedGroupedTournament
                    disciplineId={disciplineId}
                    locationId={locationId}
                    tournamentId={getTournamentId}
                    sportPageType={sportPageType}
                    eventCategoryId={eventCategoryId}
                    activeTab={getActiveTab}
                    mainMarketsLimit={mainMarketsLimit}
                    deviceType={deviceType}
                    handleTournamentParams={handleSetTournamentParams}
                    tournamentParams={getTournamentParams}
                />
            </UserBehaviorTrackingContext.Provider>
        </div>
    );
}

export const TournamentPage: FC<TournamentPagePublicProps> = compose(
    withSelectionBetslipData<TournamentPagePublicProps>(),
    connect<TournamentPageStoreProps, undefined, TournamentPagePublicProps>(
        (state: AppState) => {
            return {
                isLarge: state.breakpoints.large || state.breakpoints.xl,
                breakpoints: state.breakpoints,
                deviceType: state.deviceInfo?.platform.type,
                routeName: state.routing.name,
            };
        }
    ),
)(TournamentPageComponent);
